import { useLazyQuery, useMutation } from '@apollo/client';
import { useDetailCache } from 'cache/useDetailCache';
import {
    CreateWorldStory,
    CreateWorldStoryVariables
} from 'graphql/mutation/__generated__/CreateWorldStory';
import {
    RemoveWorldStory,
    RemoveWorldStoryVariables
} from 'graphql/mutation/__generated__/RemoveWorldStory';
import {
    UpdateWorldStory,
    UpdateWorldStoryVariables
} from 'graphql/mutation/__generated__/UpdateWorldStory';
import {
    CREATE_WORLD_STORY_MUTATION,
    REMOVE_WORLD_STORY_MUTATION,
    UPDATE_WORLD_STORY_MUTATION
} from 'graphql/mutation/momentMutation';
import { DetailQuery, DetailQueryVariables } from 'graphql/query/__generated__/DetailQuery';
import { DETAIL_QUERY } from 'graphql/query/detailQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { IUpdateDetailInput } from 'libs/types/detail/detailTypes';
import { omitAnything } from 'libs/utils/detail/detailInput';

export const useDetailCRUD = (mapId: string) => {
    const detailCache = useDetailCache(mapId);
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const [read] = useLazyQuery<DetailQuery, DetailQueryVariables>(DETAIL_QUERY);

    const [create] = useMutation<CreateWorldStory, CreateWorldStoryVariables>(
        CREATE_WORLD_STORY_MUTATION
    );

    const [update] = useMutation<UpdateWorldStory, UpdateWorldStoryVariables>(
        UPDATE_WORLD_STORY_MUTATION
    );

    const [remove] = useMutation<RemoveWorldStory, RemoveWorldStoryVariables>(
        REMOVE_WORLD_STORY_MUTATION
    );

    async function createDetail(
        variables: Omit<CreateWorldStoryVariables, 'worldMapId' | 'token'>
    ) {
        return create({
            variables: {
                ...variables,
                worldMapId: mapId,
                token: getTokenForMapId(mapId)
            },
            update: (cache, result) => {
                detailCache.addDetailListCache(cache, result.data.createWorldStory);
            }
        });
    }

    async function deleteDetail(detailId: string) {
        return remove({
            variables: {
                worldMapId: mapId,
                storyId: detailId,
                token: getTokenForMapId(mapId)
            },
            update: (cache) => {
                detailCache.removeDetailListCache(cache, detailId);
            }
        });
    }

    async function updateDetail(updateDetailInput: IUpdateDetailInput) {
        return update({
            variables: {
                worldMapId: mapId,
                cards: updateDetailInput.cards?.map((card) => {
                    return omitAnything(card, ['__typename', 'cardId']);
                }),
                story: updateDetailInput.detailInput,
                storyId: updateDetailInput.detailId,
                token: getTokenForMapId(mapId)
            },
            update: (cache, result) => {
                detailCache.updateDetailCache(cache, result.data.updateWorldStory);
            }
        });
    }

    function readDetail(detailId: string) {
        return read({
            variables: {
                worldMapId: mapId,
                storyId: detailId,
                token: getTokenForMapId(mapId)
            }
        });
    }

    return {
        createDetail,
        deleteDetail,
        updateDetail,
        readDetail
    };
};
